import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, tokenRequest } from '../config/msalConfig';

const msalInstance = new PublicClientApplication(msalConfig);
const Component = {

  getMsalInstance: function () {
    return msalInstance;
  },

  /**
   * Initialize MSAL and handle redirect response
   */
  initializeMsal: async function () {
    try {
      const response = await msalInstance.handleRedirectPromise();
      return response;
    } catch (error) {
      // "no_token_request_cache_error" is not critical - it just means there's no redirect to process
      // This happens normally on page refresh or direct navigation
      if (error.errorCode === 'no_token_request_cache_error') {
        console.debug('No token request in cache - not processing redirect');
        return null;
      }

      console.error('MSAL initialization error:', error);
      throw error;
    }
  },

  /**
   * Redirect user to Entra ID login page
   */
  redirectToLoginPage: async function () {
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect error:', error);
      throw error;
    }
  },

  /**
   * Logout user and redirect to Entra ID logout page
   */
  logout: async function () {
    const account = msalInstance.getActiveAccount();
    try {
      await msalInstance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: clear local cache and redirect manually
      this.clearSessionStartTime();
      window.location.href = process.env.REACT_APP_REDIRECT_URI;
    }
  },

  /**
   * Get ID token from MSAL cache
   */
  getIdToken: function () {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }
    return account.idToken;
  },

  /**
   * Get access token asynchronously using MSAL acquireTokenSilent
   * This is the proper way to get access tokens with MSAL
   */
  getAccessToken: async function () {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn('No active account found when trying to get access token');
      return null;
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });

      return response.accessToken;
    } catch (error) {
      console.error('Error acquiring access token silently:', error);

      // If silent token acquisition fails, we might need interactive login
      if (error.name === 'InteractionRequiredAuthError') {
        console.warn('Interaction required for token acquisition');
      }

      return null;
    }
  },

  /**
   * Get access token synchronously from cache (for immediate use)
   * DEPRECATED: Use getAccessToken() instead for proper token acquisition
   */
  getAccessTokenSync: function () {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      // Iterate through sessionStorage to find access token
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);

        // MSAL v2 stores access tokens with keys containing 'accesstoken'
        if (key && key.includes('accesstoken') && key.includes(msalConfig.auth.clientId)) {
          try {
            const item = sessionStorage.getItem(key);
            if (item) {
              const tokenData = JSON.parse(item);

              // MSAL v2 structure: { secret: "token", credentialType: "AccessToken", ... }
              if (tokenData.secret && tokenData.credentialType === 'AccessToken') {
                // Check if token is not expired
                if (tokenData.expiresOn) {
                  const expirationTime = parseInt(tokenData.expiresOn) * 1000; // Convert to milliseconds
                  const now = Date.now();

                  if (expirationTime > now) {
                    return tokenData.secret;
                  }
                } else {
                  // If no expiration info, return the token anyway
                  return tokenData.secret;
                }
              }
            }
          } catch (parseError) {
            // Skip this key if parsing fails
            continue;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error reading access token from cache:', error);
      return null;
    }
  },

  /**
   * Get token payload (decoded JWT claims)
   */
  getTokenPayload: function () {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }
    return account.idTokenClaims;
  },

  /**
   * Get username from token claims
   */
  getUserName: function () {
    const payload = this.getTokenPayload();
    if (!payload) {
      return null;
    }

    if (payload.preferred_username) {
      return payload.preferred_username.split('@')[0];
    }

    if (payload.email) {
      return payload.email.split('@')[0];
    }

    if (payload.name) {
      return payload.name;
    }

    return 'Unknown User';
  },

  setSessionStartTime: function (datetime) {
    window.localStorage.setItem('sessionStartTime', datetime);
  },

  getSessionStartTime: function () {
    return window.localStorage.getItem('sessionStartTime');
  },

  clearSessionStartTime: function () {
    window.localStorage.removeItem('sessionStartTime');
  },
};

export default Component;
